(() => {
  const desktopHover = window.matchMedia('(min-width: 990px) and (hover: hover) and (pointer: fine)');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const CLOSE_ANIMATION_MS = 280;
  const HOVER_CLOSE_DELAY_MS = 110;

  const syncHeaderState = (wrapper) => {
    if (!wrapper) return;
    const hasOpenMegaMenu = Boolean(wrapper.querySelector('header-menu > details[open]'));
    wrapper.classList.toggle('soul-header-wrapper--menu-open', hasOpenMegaMenu);
  };

  const activateMegaPromo = (details, key) => {
    if (!details || !key) return;

    const links = Array.from(details.querySelectorAll('.soul-mega-menu__link[data-soul-mega-key]'));
    const cards = Array.from(details.querySelectorAll('.soul-mega-promo--desktop[data-soul-mega-card]'));
    const targetCard = cards.find((card) => card.dataset.soulMegaCard === key);

    if (!targetCard) return;

    links.forEach((link) => {
      link.classList.toggle('soul-mega-menu__link--preview-active', link.dataset.soulMegaKey === key);
    });

    cards.forEach((card) => {
      const isActive = card === targetCard;
      card.classList.toggle('soul-mega-promo--active', isActive);
      card.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      card.tabIndex = isActive ? 0 : -1;
    });

    details.dataset.soulMegaActiveKey = key;
  };

  const resetMegaPromo = (details) => {
    if (!details?.classList.contains('soul-mega-menu')) return;

    const firstLink = details.querySelector('.soul-mega-menu__link[data-soul-mega-key]');
    if (firstLink?.dataset.soulMegaKey) {
      activateMegaPromo(details, firstLink.dataset.soulMegaKey);
    }
  };

  const bindDynamicMegaPromo = (details) => {
    if (!details?.classList.contains('soul-mega-menu') || details.dataset.soulPromoBound === 'true') return;

    details.dataset.soulPromoBound = 'true';
    const links = details.querySelectorAll('.soul-mega-menu__link[data-soul-mega-key]');

    links.forEach((link) => {
      const activate = () => activateMegaPromo(details, link.dataset.soulMegaKey);
      link.addEventListener('mouseenter', activate);
      link.addEventListener('focus', activate);
    });

    resetMegaPromo(details);
  };

  const clearCloseAnimation = (details) => {
    if (!details?._soulCloseAnimationTimer) return;
    window.clearTimeout(details._soulCloseAnimationTimer);
    details._soulCloseAnimationTimer = null;
  };

  const clearOpenFrames = (details) => {
    if (!details) return;

    if (details._soulOpenFrameOne) {
      window.cancelAnimationFrame(details._soulOpenFrameOne);
      details._soulOpenFrameOne = null;
    }

    if (details._soulOpenFrameTwo) {
      window.cancelAnimationFrame(details._soulOpenFrameTwo);
      details._soulOpenFrameTwo = null;
    }
  };

  const revealMegaMenu = (details) => {
    if (!details?.classList.contains('soul-mega-menu')) return;

    clearOpenFrames(details);

    if (reducedMotion.matches) {
      details.classList.add('soul-mega-menu--visible');
      return;
    }

    // Two frames make sure the closed visual state is painted first,
    // so the first opening animates just as smoothly as later openings.
    details._soulOpenFrameOne = window.requestAnimationFrame(() => {
      details._soulOpenFrameOne = null;
      details._soulOpenFrameTwo = window.requestAnimationFrame(() => {
        details._soulOpenFrameTwo = null;
        if (details.open && !details.classList.contains('soul-mega-menu--closing')) {
          details.classList.add('soul-mega-menu--visible');
        }
      });
    });
  };

  const finishCloseMegaMenu = (details) => {
    clearCloseAnimation(details);
    clearOpenFrames(details);

    details.open = false;
    details.classList.remove('soul-mega-menu--visible', 'soul-mega-menu--closing');
    details.querySelector(':scope > summary')?.setAttribute('aria-expanded', 'false');
    syncHeaderState(details.closest('.soul-header-wrapper'));
  };

  const closeMegaMenu = (details, immediate = false) => {
    if (!details?.open) return;

    const isAnimatedMegaMenu = details.classList.contains('soul-mega-menu') && desktopHover.matches;

    if (immediate || !isAnimatedMegaMenu || reducedMotion.matches) {
      finishCloseMegaMenu(details);
      return;
    }

    clearCloseAnimation(details);
    clearOpenFrames(details);

    details.classList.add('soul-mega-menu--closing');
    details.classList.remove('soul-mega-menu--visible');
    details.querySelector(':scope > summary')?.setAttribute('aria-expanded', 'false');

    // Keep <details open> during the visual close animation. Removing it
    // immediately would make the browser hide the mega menu before CSS can animate.
    details._soulCloseAnimationTimer = window.setTimeout(() => {
      if (details.classList.contains('soul-mega-menu--closing')) {
        finishCloseMegaMenu(details);
      }
    }, CLOSE_ANIMATION_MS);
  };

  const openMegaMenu = (details) => {
    if (!desktopHover.matches || !details) return;

    const wrapper = details.closest('.soul-header-wrapper');

    wrapper?.querySelectorAll('header-menu > details[open]').forEach((openDetails) => {
      if (openDetails !== details) closeMegaMenu(openDetails, true);
    });

    clearCloseAnimation(details);
    clearOpenFrames(details);
    details.classList.remove('soul-mega-menu--closing');

    const wasOpen = details.open;

    if (!wasOpen) {
      details.classList.remove('soul-mega-menu--visible');
      details.open = true;
      resetMegaPromo(details);
    }

    details.querySelector(':scope > summary')?.setAttribute('aria-expanded', 'true');
    syncHeaderState(wrapper);

    if (details.classList.contains('soul-mega-menu')) {
      if (wasOpen) {
        // If the pointer comes back while the menu is fading out,
        // smoothly reverse the transition instead of restarting it.
        details.classList.add('soul-mega-menu--visible');
      } else {
        revealMegaMenu(details);
      }
    }
  };

  const bindHoverMenus = (root = document) => {
    root.querySelectorAll('header-menu').forEach((menu) => {
      if (menu.dataset.soulHoverBound === 'true') return;

      const details = menu.querySelector(':scope > details');
      if (!details) return;

      menu.dataset.soulHoverBound = 'true';
      bindDynamicMegaPromo(details);

      const summary = details.querySelector(':scope > summary');
      let closeTimer;

      menu.addEventListener('mouseenter', () => {
        window.clearTimeout(closeTimer);
        openMegaMenu(details);
      });

      menu.addEventListener('mouseleave', () => {
        window.clearTimeout(closeTimer);
        closeTimer = window.setTimeout(() => {
          closeMegaMenu(details);
        }, HOVER_CLOSE_DELAY_MS);
      });

      // On desktop, pointer clicks should not instantly toggle <details>
      // and bypass our close animation. Keyboard activation still toggles it.
      summary?.addEventListener('click', (event) => {
        if (!desktopHover.matches || !details.classList.contains('soul-mega-menu')) return;

        event.preventDefault();

        if (event.detail === 0) {
          if (details.open && details.classList.contains('soul-mega-menu--visible')) {
            closeMegaMenu(details);
          } else {
            openMegaMenu(details);
          }
          return;
        }

        openMegaMenu(details);
      });

      details.addEventListener('toggle', () => {
        syncHeaderState(details.closest('.soul-header-wrapper'));

        if (details.open) {
          resetMegaPromo(details);

          if (
            details.classList.contains('soul-mega-menu') &&
            desktopHover.matches &&
            !details.classList.contains('soul-mega-menu--visible') &&
            !details.classList.contains('soul-mega-menu--closing')
          ) {
            revealMegaMenu(details);
          }
        } else {
          clearCloseAnimation(details);
          clearOpenFrames(details);
          details.classList.remove('soul-mega-menu--visible', 'soul-mega-menu--closing');
        }
      });
    });
  };

  document.addEventListener('DOMContentLoaded', () => bindHoverMenus());
  document.addEventListener('shopify:section:load', (event) => bindHoverMenus(event.target));

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    document.querySelectorAll('.soul-header-wrapper header-menu > details[open]').forEach((details) => {
      closeMegaMenu(details);
    });
  });
})();
